import { putOrgMemberFeaturePreviewsMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { Logger, trackAction } from '@onefootprint/logger';
import type { FeaturePreview, FeaturePreviewChoice } from '@onefootprint/request-types/dashboard';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import useSession, { useStore } from '../use-session';
import FEATURE_PREVIEWS, { getFeaturePreviewContextKey } from './feature-previews';

/**
 * Writes one preview's choice to the store, or clears it. Reads the store at call time, not a render's copy, and
 * skips it when someone else has signed in meanwhile. Only this preview changes, so a slower save can't undo another.
 */
const storeChoice = (userId: string | undefined, preview: FeaturePreview, choice?: FeaturePreviewChoice) => {
  const {
    user
  } = useStore.getState().data;
  if (!user || user.id !== userId) {
    return;
  }
  const others = (user.featurePreviews ?? []).filter(item => item.preview !== preview);
  useStore.getState().update({
    user: {
      ...user,
      featurePreviews: choice ? [...others, choice] : others
    }
  });
};

/** Uses the saved choice, or the preview's default when the user has not chosen. */
const useFeaturePreview = (preview: FeaturePreview) => {
  const {
    data
  } = useSession();
  const showRequestErrorToast = useRequestErrorToast();
  const {
    defaultEnabled
  } = FEATURE_PREVIEWS[preview];
  const userId = data.user?.id;
  const choice = data.user?.featurePreviews?.find(item => item.preview === preview);
  const isEnabled = choice?.enabled ?? defaultEnabled;
  const mutationKey = ['feature-preview', userId, preview];

  // Switches right away and switches back if the save fails. These callbacks sit here rather than in mutate()
  // because React Query skips mutate()'s once the page that called it unmounts.
  const mutation = useMutation({
    ...putOrgMemberFeaturePreviewsMutation(),
    mutationKey,
    onMutate: ({
      body
    }) => {
      storeChoice(userId, preview, body);
      return {
        previous: choice
      };
    },
    onError: (error, _variables, context) => {
      storeChoice(userId, preview, context?.previous);
      showRequestErrorToast(error);
    },
    onSuccess: (_data, {
      body: body_0
    }) => {
      storeChoice(userId, preview, body_0);
      trackAction('feature_preview_switched', {
        preview,
        enabled: body_0.enabled
      });
    }
  });

  // Tags every Datadog RUM event with the experience this user sees, so usage can be split by it.
  useEffect(() => {
    Logger.appendGlobalContext({
      [getFeaturePreviewContextKey(preview)]: isEnabled
    });
  }, [preview, isEnabled]);
  const setEnabled = (enabled: boolean) => mutation.mutate({
    body: {
      preview,
      enabled
    }
  });

  // Read from every save of this preview, not this hook's own: the switch remounts the menu that started it.
  const isSaving = useIsMutating({
    mutationKey
  }) > 0;
  return {
    isEnabled,
    setEnabled,
    isSaving
  };
};
export default useFeaturePreview;